import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import { paramProblem } from "./resolve";
import { SceneDialog } from "../../scenes/SceneDialog";
import type { DeviceActions } from "./useDeviceActions";

/**
 * Before a command goes (WEB-CMD-04, -05): a guarded command shows the profile's confirm text and the device's name,
 * and a command with params asks for them within the profile's limits. Only Confirm sends.
 */
export function ActionDialog({ actions, nameOf }: { actions: DeviceActions; nameOf: (device: string) => string }) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const fieldId = useId();
  const { asking, closeAsking, send } = actions;
  const [values, setValues] = useState<Record<string, string>>({});
  const [tried, setTried] = useState(false);

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (asking && !d.open) {
      setValues({});
      setTried(false);
      d.showModal();
    } else if (!asking && d.open) d.close();
  }, [asking]);

  const resolved = asking?.resolved.kind === "command" ? asking.resolved : undefined;
  const ask = resolved?.ask ?? [];
  const problems = Object.fromEntries(ask.map((p) => [p.name, paramProblem(p, values[p.name] ?? "")]));
  const device = asking ? nameOf(asking.action.device) : "";

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!asking || !resolved) return;
    setTried(true);
    if (Object.values(problems).some(Boolean)) return;
    void send(asking.action, resolved, Object.fromEntries(ask.map((p) => [p.name, Number(values[p.name])])));
    closeAsking();
  }

  return (
    <>
      <SceneDialog scenes={actions.scenes} nameOf={(id) => actions.scenes.list?.[id]?.name ?? id} />
      <dialog
        ref={dialog}
        onClose={closeAsking}
        aria-labelledby={titleId}
        className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-black/40"
      >
        {resolved && (
          <form onSubmit={submit} noValidate className="p-6">
            <h2 id={titleId} className="text-lg font-semibold text-gray-900">
              {t(resolved.guarded ? "controls.confirm.title" : "controls.params.title", { device })}
            </h2>
            {resolved.guarded && (
              <p className="mt-3 text-sm text-gray-900">
                {resolved.confirmText ??
                  t("controls.confirm.text", { command: resolved.label ?? resolved.command, device })}
              </p>
            )}
            {ask.map((p) => {
              const id = `${fieldId}-${p.name}`;
              const problem = tried ? problems[p.name] : undefined;
              return (
                <div key={p.name} className="mt-4">
                  <label htmlFor={id} className="block text-sm font-medium text-gray-900">
                    {p.unit ? `${p.name} (${p.unit})` : p.name}
                  </label>
                  <input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    min={p.min}
                    max={p.max}
                    value={values[p.name] ?? ""}
                    aria-invalid={problem ? true : undefined}
                    aria-describedby={`${id}-hint`}
                    onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.value }))}
                    className="mt-1 w-40 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  />
                  <p id={`${id}-hint`} className={`mt-1 text-xs ${problem ? "text-red-800" : "text-gray-700"}`}>
                    {problem === "required"
                      ? t("controls.params.required")
                      : p.min !== undefined && p.max !== undefined
                        ? t("controls.params.range", { min: p.min, max: p.max })
                        : ""}
                  </p>
                </div>
              );
            })}
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={closeAsking}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t(resolved.guarded ? "controls.confirm.go" : "controls.params.send")}</Button>
            </div>
          </form>
        )}
      </dialog>
    </>
  );
}
